import { Controller } from "@hotwired/stimulus"

// Resume Translator — Veridian dashboard tool.
//
// Ports the former Alpine component verbatim: upload a PDF, pick a target
// language, POST to /ai-resume-translator/translate, poll /status/:job_id
// until the job completes, then expose Copy / .txt / PDF export. The server
// contract (endpoints, params, response shapes) is unchanged.
export default class extends Controller {
  static targets = [
    "fileInput", "fileName", "fileSize", "removeFile", "pasteText",
    "languageDropdown", "languageButton", "languageButtonLabel", "languageFlag",
    "languageMenu", "languageInput",
    "translateButton", "spinner", "buttonLabel",
    "statusMessage", "error", "success",
    "output", "pdfButton", "emptyState", "previewNote"
  ]

  static values = {
    translateUrl: { type: String, default: "/ai-resume-translator/translate" },
    statusUrl:    { type: String, default: "/ai-resume-translator/status" },
    pdfUrl:       { type: String, default: "/ai-resume-translator/pdf" }
  }

  connect() {
    this.jobId = ""
    this.status = ""
    this.pdfReady = false
    this.previewAvailable = true
    this.isTranslating = false
    this.resumeFile = null
    this.onOutsideClick = this.onOutsideClick.bind(this)
    document.addEventListener("click", this.onOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onOutsideClick)
  }

  // ── language dropdown (custom listbox with country flags) ──────
  toggleLanguageMenu() {
    const nowHidden = this.languageMenuTarget.classList.toggle("hidden")
    this.languageButtonTarget.setAttribute("aria-expanded", nowHidden ? "false" : "true")
  }

  closeLanguageMenu() {
    this.languageMenuTarget.classList.add("hidden")
    this.languageButtonTarget.setAttribute("aria-expanded", "false")
  }

  selectLanguage(event) {
    const option = event.currentTarget
    this.languageInputTarget.value = option.dataset.code
    this.languageButtonLabelTarget.textContent = option.dataset.label
    const flag = option.querySelector("svg")
    if (flag) this.languageFlagTarget.replaceChildren(flag.cloneNode(true))
    this.closeLanguageMenu()
  }

  onOutsideClick(event) {
    if (this.hasLanguageDropdownTarget && !this.languageDropdownTarget.contains(event.target)) {
      this.closeLanguageMenu()
    }
  }

  handleFileChange(event) {
    const file = event.target.files[0]
    if (!file) return
    this.resumeFile = file
    this.fileNameTarget.textContent = file.name
    this.fileSizeTarget.textContent = `${(file.size / (1024 * 1024)).toFixed(2)} MB`
    this.removeFileTarget.classList.remove("hidden")
  }

  clearFile(event) {
    if (event) event.preventDefault()
    this.resumeFile = null
    this.fileInputTarget.value = ""
    this.fileNameTarget.textContent = "Upload PDF"
    this.fileSizeTarget.textContent = "Max 10MB. Keeps your original layout."
    this.removeFileTarget.classList.add("hidden")
  }

  async translate() {
    this.clearMessages()
    this.outputTarget.value = ""
    this.hideOutput()
    this.jobId = ""
    this.status = ""
    this.pdfReady = false
    this.previewAvailable = true
    this.updatePdfButton()

    const language = this.languageInputTarget.value
    if (!language) { this.showError("Select a target language."); return }
    if (!this.resumeFile) { this.showError("Upload a resume file to keep layout in the PDF."); return }

    this.setTranslating(true)
    try {
      const formData = new FormData()
      formData.append("language", language)
      formData.append("resume_file", this.resumeFile)

      const response = await fetch(this.translateUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken() },
        body: formData
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Translation failed.")
      }
      this.jobId = data.job_id || ""
      this.status = data.status || "queued"
      await this.poll()
    } catch (error) {
      this.showError(error.message || "Translation failed.")
    } finally {
      this.setTranslating(false)
    }
  }

  async poll() {
    const startedAt = Date.now()
    const timeoutMs = 180000
    // The job flips "queued" -> "processing" the instant a worker picks it up.
    // If it's still "queued" past this window, no worker is consuming the queue
    // (or it's badly backed up), so fail fast instead of waiting out timeoutMs.
    const queuedGraceMs = 30000

    while (true) {
      if (!this.jobId) throw new Error("Translation job is missing.")
      if (Date.now() - startedAt > timeoutMs) {
        throw new Error("Translation is taking longer than expected. Please try again.")
      }

      const response = await fetch(`${this.statusUrlValue}/${this.jobId}`)
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Translation failed.")
      }

      this.status = data.status || "processing"
      this.pdfReady = !!data.pdf_ready
      this.previewAvailable = data.preview_available !== false
      this.updatePdfButton()

      if (this.status === "queued" && Date.now() - startedAt > queuedGraceMs) {
        throw new Error("Translation hasn't started — the translation service looks busy or offline. Please try again in a moment.")
      }
      if (data.status === "error") throw new Error(data.error || "Translation failed.")
      if (data.status === "completed") {
        this.outputTarget.value = data.translated_text || ""
        this.showOutput()
        this.previewNoteTarget.classList.toggle("hidden", this.previewAvailable)
        this.showSuccess(`Translation complete in ${this.languageLabel()}.`)
        return
      }

      this.statusMessageTarget.classList.remove("hidden")
      this.statusMessageTarget.textContent =
        this.status === "queued" ? "Queued for translation…" : "Translating your resume…"

      await new Promise((resolve) => setTimeout(resolve, 1500))
    }
  }

  async copy() {
    this.clearMessages()
    const text = this.outputTarget.value || ""
    if (!text.trim()) { this.showError("Translate a resume first."); return }
    try {
      await navigator.clipboard.writeText(text)
      this.showSuccess("Translation copied to clipboard.")
    } catch (_) {
      this.showError("Copy failed. Please select and copy manually.")
    }
  }

  downloadTxt() {
    this.clearMessages()
    const text = this.outputTarget.value || ""
    if (!text.trim()) { this.showError("Translate a resume first."); return }
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" })
    this.downloadBlob(blob, `resume-translation-${this.languageCode()}.txt`)
    this.showSuccess("Translation downloaded.")
  }

  async downloadPdf() {
    this.clearMessages()
    if (!this.jobId || !this.pdfReady || this.status !== "completed") {
      this.showError("Translation PDF is not ready yet.")
      return
    }
    try {
      const response = await fetch(`${this.pdfUrlValue}/${this.jobId}`)
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || "PDF download failed.")
      }
      const blob = await response.blob()
      this.downloadBlob(blob, `resume-translation-${this.languageCode()}.pdf`)
      this.showSuccess("PDF downloaded.")
    } catch (error) {
      this.showError(error.message || "PDF download failed.")
    }
  }

  // ── helpers ────────────────────────────────────────────────────
  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  languageCode() {
    return this.languageInputTarget.value || "output"
  }

  languageLabel() {
    return this.languageButtonLabelTarget.textContent.trim() || this.languageCode()
  }

  setTranslating(on) {
    this.isTranslating = on
    this.translateButtonTarget.disabled = on
    this.spinnerTarget.classList.toggle("hidden", !on)
    this.buttonLabelTarget.textContent = on ? "Translating…" : "Translate Resume"
    if (!on) this.statusMessageTarget.classList.add("hidden")
  }

  updatePdfButton() {
    const ready = this.pdfReady && this.status === "completed"
    this.pdfButtonTarget.disabled = !ready
    this.pdfButtonTarget.classList.toggle("opacity-50", !ready)
    this.pdfButtonTarget.classList.toggle("cursor-not-allowed", !ready)
  }

  showOutput() {
    this.outputTarget.classList.remove("hidden")
    this.emptyStateTarget.classList.add("hidden")
  }

  hideOutput() {
    this.outputTarget.classList.add("hidden")
    this.emptyStateTarget.classList.remove("hidden")
    this.previewNoteTarget.classList.add("hidden")
  }

  downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  clearMessages() {
    this.errorTarget.classList.add("hidden")
    this.errorTarget.textContent = ""
    this.successTarget.classList.add("hidden")
    this.successTarget.textContent = ""
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  showSuccess(message) {
    this.successTarget.textContent = message
    this.successTarget.classList.remove("hidden")
  }
}
